import styled, { css } from "styled-components";
import { Heading } from "../../components/Heading/Heading";
import { Section } from "../../components/Section/Section";
import { Subheading } from "../../components/Subheading/Subheading";
import { Text } from "../../components/Text/Text";
import { Title } from "../../components/Title/Title";
import { BENTO_SPAN_COLUMNS, BENTO_SPAN_ROWS, type BentoSpan } from "./bentoLayout";
import {
  HIGHLIGHT_MAX_HEIGHT,
  type HighlightsDetailsVariant,
  type HighlightsFormat,
  type PaddingProps,
} from "./types";

type Align = "start" | "center";

/**
 * O `PaddingProps` do contrato guarda os valores como string (é o que o Craft
 * serializa), enquanto o `Section` compartilhado espera number. A conversão
 * mora aqui para não mexer na interface pública do bloco.
 */
export type SectionPadding = {
  typeMeasure?: string;
  desktop?: { paddingTop?: number; paddingBottom?: number };
  mobile?: { paddingTop?: number; paddingBottom?: number };
};

const toNumber = (value?: string | number): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const toSectionPadding = (padding?: PaddingProps): SectionPadding | undefined => {
  if (!padding) return undefined;

  return {
    typeMeasure: padding.typeMeasure,
    desktop: {
      paddingTop: toNumber(padding.desktop?.paddingTop ?? padding.desktop?.top),
      paddingBottom: toNumber(padding.desktop?.paddingBottom ?? padding.desktop?.bottom),
    },
    mobile: {
      paddingTop: toNumber(padding.mobile?.paddingTop ?? padding.mobile?.top),
      paddingBottom: toNumber(padding.mobile?.paddingBottom ?? padding.mobile?.bottom),
    },
  };
};

const alignItems = (align?: Align) =>
  align === "center" ? "center" : "flex-start";
const textAlign = (align?: Align) => (align === "center" ? "center" : "left");

/* ---------------------------------------------------------------------------
   Seção + coluna de conteúdo padrão (CLAUDE.md → "Coluna de conteúdo padrão")
   Wrapper com gutters responsivos; container 76rem centralizado.
--------------------------------------------------------------------------- */

interface HighlightsSectionProps extends Section {
  backgroundcolor?: string;
}

export const HighlightsSection = styled(Section)<HighlightsSectionProps>`
  position: relative;
  width: 100%;
  overflow-x: clip;
  background-color: ${({ backgroundcolor }) =>
    backgroundcolor || "var(--general-background)"};
`;

export const ContentColumn = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  max-width: 76rem;

  @media (min-width: 1216px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  @media (min-width: 576px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }
  @media (max-width: 767px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

/**
 * Container interno que carrega o `data-anim-stagger` (nunca no root).
 *
 * Sem `align-items` aqui de propósito: com `align-items: center` o browser
 * resolve o `text-align` dos descendentes como `-webkit-center`, e o
 * alinhamento do CABEÇALHO acabava centralizando também o texto dos cards.
 * Quem alinha o cabeçalho é o próprio `Header`.
 */
export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/*
 * Cabeçalho + navegação na MESMA linha (padrão do bloco 38): o texto à esquerda
 * e as setas à direita, nos dois extremos. Com alinhamento centralizado o texto
 * centraliza e as setas descem para o rodapé do carrossel (`BottomNav`).
 */
/**
 * Respiro entre o bloco de cards e o que vem antes/depois dele — cabeçalho em
 * cima, indicador e setas embaixo.
 *
 * É UM valor só, de propósito: o indicador usava 1.5rem enquanto o cabeçalho
 * usava 2rem no desktop, e a assimetria fazia o indicador parecer colado no
 * carrossel. Mexeu num lado, mexeu nos dois.
 */
const CARDS_SPACING = css`
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const CARDS_SPACING_TOP = css`
  margin-top: 1.5rem;

  @media (min-width: 768px) {
    margin-top: 2rem;
  }
`;

export const Header = styled.div<{ align?: Align }>`
  display: flex;
  align-items: flex-end;
  justify-content: ${({ align }) => (align === "center" ? "center" : "space-between")};
  gap: 1rem;
  width: 100%;
  ${CARDS_SPACING}
`;

export const HeaderText = styled.div<{ align?: Align }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  flex: ${({ align }) => (align === "center" ? "0 1 auto" : "1 1 auto")};
  align-items: ${({ align }) => alignItems(align)};
  text-align: ${({ align }) => textAlign(align)};
`;

/* Setas na linha do cabeçalho — somem no mobile (vão para o rodapé). */
/**
 * Recuo que a área de clique do botão impõe ao glifo.
 *
 * O botão sem disco tem 32px para um glifo de 22px, ou seja 5px de folga de
 * cada lado. O contador e o título são texto e encostam na borda do container;
 * a seta, não — o traço começa 5px adentro, e as duas linhas ficam
 * visivelmente desalinhadas. Com disco a borda do círculo marca a extremidade
 * e o olho aceita, por isso a compensação vale só no modo sem moldura.
 *
 * Só à esquerda: centralizado, a folga se cancela nos dois lados.
 */
const BARE_ARROW_INSET = "5px";

export const HeaderNav = styled.div<{ $bare?: boolean }>`
  display: flex;
  align-items: center;
  /* Sem disco o par se aproxima — ver o comentário em ModalNavButtons. */
  gap: ${({ $bare }) => ($bare ? "0" : "0.5rem")};

  /* Encostadas na direita da coluna: compensa a folga desse lado. */
  ${({ $bare }) =>
    $bare &&
    css`
      margin-right: -${BARE_ARROW_INSET};
    `}
  flex-shrink: 0;

  @media (max-width: 767px) {
    display: none;
  }
`;

/*
 * Rodapé do carrossel: é aqui que as setas ficam quando o cabeçalho está
 * centralizado (senão elas apareceriam acima da barra, fora do padrão).
 */

export const BottomNav = styled.div<{ $align?: Align; $bare?: boolean }>`
  /*
   * Aparece quando o cabeçalho é centralizado (as setas não cabem na linha do
   * título) e SEMPRE no mobile, onde o HeaderNav some. Mesma divisão do 38.
   */
  display: ${({ $align }) => ($align === "center" ? "flex" : "none")};
  width: 100%;
  align-items: center;
  justify-content: ${({ $align }) => ($align === "center" ? "center" : "flex-start")};
  gap: ${({ $bare }) => ($bare ? "0" : "0.5rem")};
  ${CARDS_SPACING_TOP}

  ${({ $align, $bare }) =>
    $bare &&
    $align !== "center" &&
    css`
      margin-left: -${BARE_ARROW_INSET};
    `}

  @media (max-width: 767px) {
    display: flex;
  }
`;

/*
 * Cabeçalho e descrição estendem os componentes compartilhados (`Heading` e
 * `Subheading`), como fazem os outros blocos: eles já trazem a escala completa
 * do styleguide — família, peso, tamanho, line-height e letter-spacing — e
 * resolvem a tag dinâmica. Redeclarar as CSS vars à mão, como estava aqui,
 * perde line-height/letter-spacing e usa a escala errada na descrição
 * (`text` em vez de `subheading`).
 */
export const SectionTitle = styled(Heading)<{ $color?: string }>`
  color: ${({ $color }) => $color || "var(--text-title)"};
`;

export const SectionDescription = styled(Subheading)<{ $color?: string }>`
  color: ${({ $color }) => $color || "var(--text-body)"};
`;

/* ---------------------------------------------------------------------------
   Card
--------------------------------------------------------------------------- */

export const CardRoot = styled.article<{
  $format: HighlightsFormat;
  $bg?: string;
  $border?: string;
  $clickable?: boolean;
  $sideBySide?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  /*
   * Faixa horizontal no formato "texto abaixo": imagem à esquerda, texto à
   * direita. Empilhar imagem e texto num card que ocupa 2 ou 3 colunas deixa a
   * mídia larguíssima e baixa, e o texto perdido embaixo.
   */
  ${({ $sideBySide }) =>
    $sideBySide &&
    css`
      @media (min-width: 768px) {
        flex-direction: row;
        align-items: stretch;
      }
    `}
  box-sizing: border-box;
  overflow: hidden;
  border-radius: var(--design-card-border-radius, 8px);
  transition: opacity 0.2s ease;

  /* Só o card clássico tem moldura: simples e sobreposto são transparentes. */
  ${({ $format, $bg, $border }) =>
    $format === "below"
      ? css`
          background-color: ${$bg || "var(--cards-background)"};
          border: var(--design-card-border-width, 1px) solid
            ${$border || "var(--cards-border)"};
        `
      : css`
          background-color: transparent;
          border: none;
        `}

  /*
   * bare: a imagem mantém o raio, mas o card não recorta nada — sem moldura
   * não há o que recortar, e o overflow hidden cortaria o foco do teclado.
   */
  ${({ $format }) =>
    $format === "bare" &&
    css`
      overflow: visible;
      border-radius: 0;
    `}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        opacity: 0.92;
      }

      &:focus-visible {
        outline: 2px solid var(--general-primary);
        outline-offset: 2px;
      }
    `}
`;

export const CardMedia = styled.div<{
  $format: HighlightsFormat;
  $aspect: string;
  $sideBySide?: boolean;
}>`
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  /*
   * UMA proporção, resolvida em JS pelo HighlightCard.
   *
   * Antes eram duas, com o mobile numa media query de 767px — que olha a
   * viewport do BROWSER. O preview mobile do builder estreita só o canvas, não
   * a janela, então a media query nunca disparava ali e trocar a altura no
   * mobile não fazia nada. O resto do bloco já media com useContainerIsMobile;
   * só a proporção tinha ficado em CSS puro.
   */
  aspect-ratio: ${({ $aspect }) => $aspect};
  /*
   * Teto para a opção "alta", que num slide largo passava de 1600px.
   *
   * O teto é uma REDE DE SEGURANÇA, não o mecanismo de altura: quando ele
   * corta, a proporção deixa de valer e a imagem desencontra do card. Quem
   * evita chegar nele é a escolha da escala por largura de célula
   * (HIGHLIGHT_SINGLE_ASPECT / HIGHLIGHT_WIDE_ASPECT no HighlightCard).
   */
  max-height: ${HIGHLIGHT_MAX_HEIGHT};
  background-color: var(--general-border, #e2e8f0);

  /* Sem moldura de card, o raio é da própria imagem. */
  ${({ $format }) =>
    $format === "bare" &&
    css`
      border-radius: var(--design-card-border-radius, 8px);
    `}

  /*
   * Lado a lado: a mídia vira coluna de largura fixa em proporção e a altura
   * vem da linha do grid (por isso aspect-ratio auto no card).
   */
  ${({ $sideBySide }) =>
    $sideBySide &&
    css`
      @media (min-width: 768px) {
        width: 60%;
        flex-shrink: 0;
        aspect-ratio: auto;
        align-self: stretch;
      }
    `}

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Sobreposto: a mídia é a camada de fundo do card inteiro. */
  ${({ $format }) =>
    $format === "overlay" &&
    css`
      position: absolute;
      inset: 0;
      aspect-ratio: auto;
    `}
`;

/** Só no formato sobreposto: gradiente da base (cor com alfa) até transparente. */
export const OverlayShade = styled.div<{ $color: string }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    ${({ $color }) => $color} 0%,
    ${({ $color }) => $color} 22%,
    transparent 78%
  );
`;

/**
 * No sobreposto, a proporção do card vem deste espaçador (a mídia é absoluta).
 *
 * O teto de altura tem que ser o MESMO do CardMedia. A mídia é absoluta e o
 * espaçador é quem dá altura ao card: com o teto só na mídia, uma proporção
 * retrato numa coluna larga (2/3 em 596px pede 894px, contra 70vh de teto)
 * esticava o card além da imagem e sobrava uma faixa vazia embaixo dela.
 */
export const OverlaySpacer = styled.div<{ $aspect: string }>`
  width: 100%;
  aspect-ratio: ${({ $aspect }) => $aspect};
  max-height: ${HIGHLIGHT_MAX_HEIGHT};
`;

export const CardBody = styled.div<{
  $format: HighlightsFormat;
  $sideBySide?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-sizing: border-box;

  /* Lado a lado: o texto ocupa o resto da faixa e centra na vertical. */
  ${({ $sideBySide }) =>
    $sideBySide &&
    css`
      @media (min-width: 768px) {
        flex: 1;
        justify-content: center;
        padding: 2rem;
      }
    `}

  ${({ $format }) =>
    $format === "overlay"
      ? css`
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          padding: 1.5rem;
        `
      : $format === "bare"
      ? css`
          flex: 1;
          padding: 1rem 0 0;
        `
      : css`
          flex: 1;
          padding: 1.25rem;
        `}
`;

export const CardTitle = styled(Title)<{ $color?: string; $overlay?: boolean }>`
  display: block;
  color: ${({ $color, $overlay }) =>
    $color || ($overlay ? "#FFFFFF" : "var(--cards-title)")};
`;

export const CardDescription = styled(Text)<{ $color?: string; $overlay?: boolean }>`
  color: ${({ $color, $overlay }) =>
    $color || ($overlay ? "rgba(255, 255, 255, 0.85)" : "var(--cards-body)")};
`;

/** Link de detalhes — link simples, sem caixa (skill §7.3). */
/*
 * Sobre a imagem o link herda a cor do TEXTO do card (branco, como o título e a
 * descrição ao lado): a cor primária da paleta sobre uma foto escura fica
 * ilegível e destoa do resto do card. No formato `below` o link está sobre o
 * fundo do card, e aí a primária é a cor certa.
 *
 * `$color` (quando o Champion escolhe uma cor) continua vencendo nos dois casos.
 */
export const DetailsLink = styled.span<{
  $color?: string;
  $overlay?: boolean;
  $variant?: HighlightsDetailsVariant;
}>`
  /*
   * Sem margin-top: o CardBody já é flex com gap, e a margem SOMAVA ao gap —
   * o link ficava mais afastado do texto do que o texto do título e, sendo o
   * último filho, ainda empurrava o fundo do card.
   *
   * margin-top auto (só quando o texto fica FORA da imagem) ancora o link na
   * base: assim os links de cards vizinhos ficam na mesma linha mesmo com
   * descrições de tamanhos diferentes. No sobreposto não se aplica — ali o
   * corpo é absoluto, colado na base da imagem.
   */
  width: fit-content;
  font-size: 0.9375rem;
  font-weight: 500;

  ${({ $color, $overlay, $variant = "link" }) => {
    /*
     * Sobre a imagem o link é BRANCO, como o título e a descrição ao lado (a
     * primária sobre foto escura fica ilegível). Branco explícito, não
     * currentColor: o link é IRMÃO do título, não filho, então herdaria do
     * CardBody — que não define cor e cairia no preto padrão.
     */
    const accent = $color || ($overlay ? "#FFFFFF" : "var(--general-primary)");

    if ($variant === "filled") {
      /*
       * Sobre a imagem o fundo é branco, então o texto precisa ser ESCURO —
       * --buttons-text é claro e sumiria. No card com fundo sólido vale o
       * par normal da paleta.
       */
      return css`
        padding: 0.625rem 1.25rem;
        border-radius: var(--design-button-border-radius, 6px);
        background-color: ${accent};
        color: ${$overlay && !$color ? "#0F172A" : "var(--buttons-text, #ffffff)"};
      `;
    }

    if ($variant === "outline") {
      return css`
        padding: 0.625rem 1.25rem;
        border-radius: var(--design-button-border-radius, 6px);
        border: 1px solid ${accent};
        color: ${accent};
      `;
    }

    return css`
      text-decoration: underline;
      text-underline-offset: 4px;
      color: ${accent};
    `;
  }}

  ${({ $overlay, $variant = "link" }) =>
    !$overlay &&
    css`
      margin-top: auto;

      /*
       * O respiro extra é só do LINK (que não tem caixa). Nas variantes com
       * caixa ele sobrescrevia o topo do padding e deixava o botão com
       * 0.25rem em cima e 0.625rem embaixo — visivelmente torto.
       */
      ${$variant === "link" &&
      css`
        padding-top: 0.25rem;
      `}
    `}
`;

/* ---------------------------------------------------------------------------
   Grade / bento
--------------------------------------------------------------------------- */

export const Grid = styled.ul<{ $cols: number; $gap: number }>`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: grid;
  gap: ${({ $gap }) => $gap}px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: ${({ $cols }) => `repeat(${$cols}, minmax(0, 1fr))`};
  }
`;

/**
 * Bento: 1 grande ocupando 2×2 + 4 pequenos, repetindo a cada 5 itens.
 * No mobile (grade não vira carrossel) tudo cai numa coluna, com o grande primeiro
 * — que já é a ordem do DOM.
 */
export const BentoGrid = styled.ul<{ $gap: number }>`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: grid;
  gap: ${({ $gap }) => $gap}px;
  grid-template-columns: 1fr;

  /*
   * TRÊS colunas: é o que permite células de larguras diferentes na mesma
   * linha (2/3 + 1/3), que é o contraste que faz o bento parecer bento. Toda
   * composição fecha as linhas — ver bentoLayout.
   */
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const GridItem = styled.li<{ $span?: BentoSpan; $colStart?: number }>`
  min-width: 0;
  display: flex;

  @media (min-width: 768px) {
    /*
     * Primeiro item de uma última linha incompleta: começa numa coluna
     * deslocada para a sobra ficar centralizada (ver lastRowOffset). O Grid
     * não centraliza linha parcial sozinho.
     */
    ${({ $colStart }) =>
      $colStart &&
      css`
        grid-column-start: ${$colStart};
      `}

    ${({ $span }) =>
      $span &&
      $span !== "minor" &&
      css`
        grid-column: span ${BENTO_SPAN_COLUMNS[$span]};
        grid-row: span ${BENTO_SPAN_ROWS[$span]};
      `}
  }
`;

/* ---------------------------------------------------------------------------
   Carrossel (embla)
--------------------------------------------------------------------------- */

export const CarouselRoot = styled.div`
  width: 100%;
`;

/**
 * A largura da coluna de conteúdo, na fórmula do `ContentColumn`: 76rem de teto,
 * menos os gutters de 2rem em telas estreitas. Usada pelo slide em full-bleed,
 * onde o viewport deixa de ser a coluna e não serve mais de referência.
 */
const CAROUSEL_COLUMN_WIDTH = "min(100vw - 4rem, 76rem)";

/**
 * `fullBleed`: o trilho começa alinhado à coluna de conteúdo e vaza pela direita
 * da viewport (equivalente ao `p-carousel width=extended` da Porsche). Como o
 * bloco já está dentro da coluna, basta esticar a direita até a borda da viewport.
 */
export const CarouselViewport = styled.div<{ $fullBleed?: boolean; $loop?: boolean }>`
  overflow: hidden;
  width: 100%;

  ${({ $fullBleed, $loop }) =>
    $fullBleed &&
    css`
      margin-right: calc(50% - 50vw);
      width: auto;

      /*
       * O fim do trilho tem que coincidir com o fim da COLUNA — é onde a barra
       * de progresso e os indicadores terminam, e um trilho que segue além
       * deles deixa o último card solto no meio da tela.
       *
       * Com containScroll trimSnaps o embla alinha o fim do trilho com o fim do
       * viewport, e aqui o viewport foi esticado até a borda da tela. O padding
       * devolve TODA a sobra lateral (numa tela de 2560px com coluna de 1216px
       * são 672px) — antes devolvia só o gutter de 2rem, e o último slide
       * parava 640px além da coluna.
       *
       * Só sem repetição infinita: com loop não existe "último slide", o trilho
       * é contínuo e o padding não teria o que alinhar.
       */
      ${!$loop &&
      css`
        padding-right: calc(50vw - 50%);
      `}
    `}
`;

/* Espaçamento por padding-left no slide + margin-left negativo no track
   (padrão oficial do embla; `gap` no flex quebra o loop). */
export const CarouselTrack = styled.div<{ $gap: number }>`
  display: flex;
  margin-left: ${({ $gap }) => -$gap}px;
`;

/*
 * O `$basis` chega em porcentagem de UMA referência, e qual referência é
 * depende do modo:
 *
 * - contido: o viewport JÁ é a coluna de conteúdo, então 100% serve;
 * - full-bleed: o viewport foi esticado até a borda da tela, e medir o slide
 *   por ele dava um card maior que a própria coluna — o espia de 8% acabava
 *   fora da área visível útil e o carrossel parecia não ter espia nenhum.
 *
 * Por isso o full-bleed troca a referência para a largura da coluna
 * (CAROUSEL_COLUMN_WIDTH), não a do viewport. O espia volta a ser 8% do que a
 * pessoa enxerga como "a coluna", igual ao modo contido.
 */
export const CarouselSlide = styled.div<{ $basis: string; $gap: number; $fullBleed?: boolean }>`
  flex: ${({ $basis, $fullBleed }) =>
    $fullBleed
      ? `0 0 calc(${CAROUSEL_COLUMN_WIDTH} * ${$basis})`
      : `0 0 calc(100% * ${$basis})`};
  min-width: 0;
  padding-left: ${({ $gap }) => $gap}px;
  box-sizing: border-box;
  display: flex;
`;

/** Setas acima do trilho, alinhadas à direita da coluna. Só desktop. */
export const ArrowButton = styled.button<{ $bare?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /*
   * Sem disco, a área encolhe para ficar justa ao glifo: mantendo os 40px do
   * disco, os dois glifos ficavam separados por uma faixa vazia enorme e o par
   * deixava de ser lido como um controle só.
   */
  width: ${({ $bare }) => ($bare ? "32px" : "40px")};
  height: ${({ $bare }) => ($bare ? "32px" : "40px")};
  min-width: ${({ $bare }) => ($bare ? "32px" : "40px")};
  border-radius: 9999px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  /*
   * Glifo a 50% do botão com disco (o padrão web é 50-60%: Material 60%,
   * Chakra/Swiper/HIG 50%). A 40%, como estava, o ícone se perde na moldura e
   * o disco é que parece grande demais.
   */
  & svg {
    width: ${({ $bare }) => ($bare ? "22px" : "20px")};
    height: ${({ $bare }) => ($bare ? "22px" : "20px")};
  }
`;

/** Indicador abaixo do trilho, centralizado. */
/* Mesmo princípio das setas: o indicador segue o alinhamento do cabeçalho. */
export const IndicatorRow = styled.div<{ $align?: Align }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === "center" ? "center" : "flex-start")};
  /* 8px é o gap unânime dos dots no pacote (7 de 7 blocos) — não mexer. */
  gap: 0.5rem;
  width: 100%;
  ${CARDS_SPACING_TOP}
`;

/**
 * `$edge` marca a peça na borda de uma janela deslizante (quando há itens
 * demais para caber): ela encolhe, sinalizando "há mais deste lado" sem
 * precisar de reticências.
 */
type IndicatorPieceProps = {
  $active?: boolean;
  $edge?: boolean;
  $color: string;
};

/**
 * Ponto alongado: o ATIVO vira uma pílula horizontal e os inativos ficam
 * redondos (padrão de onboarding do iOS e do Airbnb).
 *
 * É o único indicador do pacote em que o ativo muda de LARGURA — por isso a
 * transição inclui `width`, não só a opacidade.
 */
export const PillDot = styled.button<IndicatorPieceProps>`
  width: ${({ $active, $edge }) => ($active ? "28px" : $edge ? "6px" : "10px")};
  height: ${({ $edge }) => ($edge ? "6px" : "10px")};
  padding: 0;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  flex-shrink: 0;
  background-color: ${({ $color }) => $color};
  opacity: ${({ $active }) => ($active ? 1 : 0.3)};
  transition: width 0.25s ease, opacity 0.2s ease;
`;

/**
 * Traço horizontal: todos do mesmo tamanho, o ativo se distingue pela
 * opacidade (padrão de stepper/story).
 *
 * Mais largo e mais grosso que o dot, e com raio pequeno em vez de total — é o
 * que o separa do `pill`, que é uma cápsula e cresce só no item ativo.
 */
export const LineDot = styled.button<IndicatorPieceProps>`
  width: ${({ $active, $edge }) => ($active ? "40px" : $edge ? "12px" : "24px")};
  height: 6px;
  padding: 0;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  flex-shrink: 0;
  background-color: ${({ $color }) => $color};
  opacity: ${({ $active }) => ($active ? 1 : 0.3)};
  transition: width 0.25s ease, opacity 0.2s ease;
`;

/**
 * Contador "1 / 4".
 *
 * `tabular-nums` é o que impede a largura de saltar ao passar de 9 para 10 —
 * sem ele o indicador "pula" no meio do carrossel.
 */
export const CounterRow = styled.div<{ $color: string }>`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  color: ${({ $color }) => $color};
  font-family: var(--typography-text-font-family);
  /* Maior no desktop, onde o indicador divide a linha com as setas de 40px. */
  font-size: 1.125rem;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;

  @media (max-width: 767px) {
    font-size: 0.9375rem;
  }
`;

/** O total e a barra ficam esmaecidos; o número atual, na cor cheia. */
export const CounterMuted = styled.span`
  opacity: 0.5;
`;

export const ProgressTrack = styled.div`
  position: relative;
  height: 6px;
  width: 100%;
  border-radius: 100px;
  overflow: hidden;
`;

export const ProgressTrackBg = styled.div<{ $color: string }>`
  position: absolute;
  inset: 0;
  border-radius: 100px;
  /*
   * Fundo = a mesma cor a 20% (o 38 usa alfa 33 no hex). Aqui vai por opacity
   * e não por concatenação: a cor pode ser uma CSS var da paleta, e
   * var(--text-title)33 não é cor válida.
   */
  background-color: ${({ $color }) => $color};
  opacity: 0.2;
`;

export const ProgressFill = styled.div<{ $color: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: 100px;
  background-color: ${({ $color }) => $color};
  transition: width 0.3s ease;
`;

/* ---------------------------------------------------------------------------
   Modal (lightbox)

   Layout: imagem solta à esquerda (padding em volta e raio próprio, derivado
   do raio do card) e, à direita, uma coluna com o texto rolando em cima e as
   setas ancoradas no rodapé. O X fica FORA do card, acima da borda superior,
   em branco fixo sobre um disco semitransparente — precisa ser legível sobre
   qualquer foto, então não segue a paleta.
--------------------------------------------------------------------------- */

/** Respiro entre a borda do card e a imagem. */
const MODAL_PAD = "1.25rem";

/**
 * Raio da imagem solta.
 *
 * A fórmula concêntrica (raio do card MENOS o padding) NÃO serve aqui: ela só
 * dá positivo quando o raio é maior que o padding, e o padding deste card
 * (1.25rem = 20px) é maior que qualquer raio que o styleguide use (4 a 16px).
 * O resultado era sempre negativo — border-radius negativo é inválido, o
 * browser descarta a declaração, e a imagem ficava de canto reto.
 *
 * Vale o mesmo raio do card, como já faz o formato Simples: as curvas ficam
 * paralelas em vez de concêntricas, e a imagem tem canto arredondado de fato.
 */
const MODAL_MEDIA_RADIUS = "var(--design-card-border-radius, 8px)";

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  /*
   * O respiro no topo é maior porque o X vive ACIMA do card (fora dele): sem
   * essa folga ele encostaria na borda da viewport.
   */
  padding: 4rem 1rem 1rem;
  background: rgba(0, 0, 0, 0.72);

  @media (max-width: 767px) {
    padding: 3.5rem 0.75rem 0.75rem;
  }
`;

/**
 * Envolve o card para dar ao X um ancoradouro SEM overflow.
 *
 * O X é posicionado acima da borda superior do card (top negativo), e o card
 * precisa de `overflow: hidden` para recortar a imagem no próprio raio — dentro
 * dele o X simplesmente sumia, recortado. Por isso a âncora é esta camada.
 */
export const ModalStage = styled.div`
  position: relative;
  width: 100%;
  max-width: 68rem;
`;

export const ModalCard = styled.div<{ $bg?: string }>`
  position: relative;
  display: grid;
  /*
   * Mídia mais larga que a coluna de texto: a imagem fica horizontal.
   *
   * O minmax(0, …) é obrigatório: um track fr tem tamanho MÍNIMO automático
   * (min-content), então uma foto muito horizontal empurrava a coluna da imagem
   * além da fração e espremia o texto para fora do card. Com o mínimo em zero a
   * proporção 1.35/1 vale sempre, seja qual for a imagem.
   */
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  /*
   * A LINHA também precisa de mínimo zero. Sem isto ela é dimensionada por
   * auto (min-content), cresce com a imagem e ignora o max-height do card: a
   * foto vazava pelo rodapé e o overflow hidden levava junto as setas, que
   * ficam no fim da coluna de texto.
   */
  grid-template-rows: minmax(0, 1fr);
  gap: ${MODAL_PAD};
  width: 100%;
  /*
   * Altura em FAIXA, não fixa.
   *
   * Com height 78vh o card crescia junto com o MONITOR e não com o texto:
   * numa tela de 1440px dava 1123px de altura para ~350px de texto, deixando
   * ~770px de branco e esticando a coluna da imagem até virar um retrato que o
   * object-fit cover recortava.
   *
   * O piso (min-height) é o que preserva a estabilidade que a altura fixa
   * buscava: textos curtos param todos na mesma altura, então o rodapé das
   * setas não pula ao navegar entre itens. Texto longo cresce até o teto e
   * passa a rolar.
   */
  min-height: 26rem;
  max-height: min(80vh, 40rem);
  padding: ${MODAL_PAD};
  box-sizing: border-box;
  overflow: hidden;
  border-radius: var(--design-card-border-radius, 8px);
  background-color: ${({ $bg }) => $bg || "var(--cards-background)"};

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
    min-height: 0;
    /* dvh, não vh: a barra do navegador móvel muda o vh durante o scroll. */
    max-height: 85dvh;
  }
`;

export const ModalMedia = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${MODAL_MEDIA_RADIUS};
  background-color: var(--general-border, #e2e8f0);
  /*
   * Altura da linha do grid, com cover: a imagem não muda de tamanho ao
   * navegar, e o recorte acompanha qualquer proporção que o racer subir.
   *
   * align-self stretch + height 100% juntos: o stretch é o que dá à
   * célula uma altura DEFINIDA para o 100% resolver contra. Sem ele, numa linha
   * auto, o 100% ficava indefinido e a foto caía no tamanho intrínseco,
   * estourando o rodapé do card.
   */
  align-self: stretch;
  height: 100%;
  min-height: 0;
  /* Par do minmax(0,…) do card: sem isto a foto ainda imporia largura própria. */
  min-width: 0;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 767px) {
    height: auto;
    aspect-ratio: 4 / 3;
  }
`;

/**
 * Coluna da direita: texto rolando em cima, setas ancoradas no rodapé.
 *
 * As setas eram `position: absolute` com `left: calc(50% + …)`, um chute que
 * NÃO batia com a grade real do card (`1.35fr 1fr` põe a divisória em ~58%, não
 * em 50%): elas ficavam ~67px à esquerda do lugar, invadindo a imagem. Numa
 * coluna flex o alinhamento sai de graça, sem porcentagem nenhuma.
 */
export const ModalTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  /*
   * O respiro da coluna de texto vive AQUI, não no ModalScroll: no filho que
   * rola, o padding-right afasta a barra de rolagem da borda e o padding-top
   * rola junto com o texto (o título encostava no topo ao rolar). Aqui ele é
   * moldura fixa.
   *
   * Maior que o padding da imagem de propósito: a foto sangra até a borda do
   * seu quadro, o texto precisa de margem para respirar.
   */
  padding: 1.75rem 0.5rem 0.25rem 1rem;

  @media (max-width: 767px) {
    padding: 1rem 0 0;
  }
`;

/** Rola sozinho; `min-height: 0` é o que permite encolher dentro do flex. */
export const ModalScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Só o respiro da barra de rolagem; a moldura é do ModalTextColumn. */
  padding-right: 0.75rem;

  @media (max-width: 767px) {
    padding-right: 0.5rem;
  }
`;

export const ModalTitle = styled(Heading)<{ $color?: string }>`
  color: ${({ $color }) => $color || "var(--cards-title)"};
`;

export const ModalText = styled.div<{ $color?: string }>`
  color: ${({ $color }) => $color || "var(--cards-body)"};
  font-family: var(--typography-text-font-family);
  font-weight: var(--typography-text-font-weight);
  font-size: var(--typography-text-font-size-desktop);
  line-height: var(--typography-text-line-height);
  letter-spacing: var(--typography-text-letter-spacing);

  @media (max-width: 767px) {
    font-size: var(--typography-text-font-size-mobile);
  }

  & > * {
    margin: 0 0 0.75rem;
  }
  & > *:last-child {
    margin-bottom: 0;
  }
`;

/** Setas do rodapé: seguem o tipo e a cor configurados no builder. */
export const ModalNavButton = styled.button<{ $bare?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /*
   * Menor que a seta do carrossel (32 contra 40): ali ela precisa se destacar
   * sobre a foto, aqui fica num rodapé de fundo sólido. Sem disco, encolhe
   * mais ainda para o par não ficar disperso.
   */
  width: ${({ $bare }) => ($bare ? "28px" : "36px")};
  height: ${({ $bare }) => ($bare ? "28px" : "36px")};
  border-radius: 9999px;

  /* No toque o alvo precisa de ~44px, então a área cresce no mobile. */
  @media (max-width: 767px) {
    width: ${({ $bare }) => ($bare ? "36px" : "44px")};
    height: ${({ $bare }) => ($bare ? "36px" : "44px")};
  }
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--general-primary);
    outline-offset: 2px;
  }

  & svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 767px) {
    & svg {
      width: 22px;
      height: 22px;
    }
  }
`;

/**
 * Rodapé da coluna de texto. O `margin-top: auto` é o ÚNICO da coluna (o
 * ModalScroll usa `flex: 1`) — dois empurrões concorrentes repartiriam a sobra
 * e nenhum encostaria no rodapé.
 */
export const ModalNavRow = styled.div<{ $bare?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: auto;
  padding-top: 0.75rem;
  padding-left: 0.25rem;
  flex-shrink: 0;
  /* Filete separando a navegação do texto que rola acima dela. */
  border-top: 1px solid var(--cards-border, rgba(127, 127, 127, 0.2));

  @media (max-width: 767px) {
    padding-left: 0.5rem;
  }
`;

export const ModalNavButtons = styled.div<{ $bare?: boolean }>`
  display: flex;
  align-items: center;
  /*
   * Sem disco o gap vai a ZERO: a folga interna do botão (5px de cada lado) já
   * separa os glifos em ~10px. O que fazia o par parecer distante nunca foi o
   * gap declarado — era a folga, que soma dos dois lados.
   */
  gap: ${({ $bare }) => ($bare ? "0" : "0.5rem")};

  /* Aqui as setas ficam à DIREITA, então a compensação é do outro lado. */
  ${({ $bare }) =>
    $bare &&
    css`
      margin-right: -${BARE_ARROW_INSET};
    `}
`;

/**
 * O X fica FORA do card, acima da borda superior, sobre o overlay escuro —
 * branco fixo num disco semitransparente, para ser legível sobre qualquer foto.
 * Ancorado no ModalStage, que não tem overflow (dentro do card ele era
 * recortado pelo `overflow: hidden`).
 */
export const ModalClose = styled.button`
  position: absolute;
  top: -3.25rem;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.28);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  & svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 767px) {
    top: -3rem;
  }
`;
